import { Card, CardContent, CardHeader } from "@/components/atomic/Card";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { cn } from "@/lib/utils";
import { ChangeType } from "@/models/vocCount";

interface Props {
  title?: string;
  totalCount: number;
  changeAmount: number;
  changeType: ChangeType;
  previousPeriodComparison?: number;
  comparisonLabelKey: string;
  className?: string;
}

/*
  組合環境用のコンポーネント

  下記の値をworkspaceのmetadataから取得する
  - total_kumiai_members: 総組合員数
  - total_enquete_responders: アントケート回答者総数
*/
const KumiaiComparisonChart: React.FC<Props> = ({ className, totalCount }) => {
  const { currentWorkspace } = useWorkspaces();

  const title = "任意項目の回答数割合";

  // workspaceのmetadataから取得
  const totalMembers = currentWorkspace?.metadata?.totalKumiaiMembers ?? 0;
  const totalResponders = currentWorkspace?.metadata?.totalEnqueteResponders ?? 0;

  // 割合計算（回答者数 / 総回答者数）
  const percentage = totalResponders > 0
    ? Math.round((totalCount / totalResponders) * 100)
    : 0;

  // 円グラフのパラメータ
  const radius = 55;
  const strokeWidth = 10;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const progressPercentage = 76; // 76%の円
  const maxProgress = (progressPercentage / 100) * circumference;
  const dashOffset = maxProgress - (maxProgress / 100) * Math.min(percentage, 100);

  return (
    <div className={cn("bg-white flex flex-row box-content", className)}>
      <Card className="w-[340px] h-[200px] relative border-0 rounded-[10px] shadow-none">
        <CardHeader className="px-4 pt-4 pb-2 text-[13px] font-bold leading-[1.6] text-center">
          {title ?? "任意項目の回答数割合"}
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex justify-center px-5 items-center gap-4">
            {/* Left side - Circular progress indicator */}
            <div className="relative w-[110px] h-[110px]">
              <svg
                width="110"
                height="110"
                viewBox="0 0 110 110"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient
                    id="kumiaiCircleGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#4BDFDB" />
                    <stop offset="100%" stopColor="#5871FE" />
                  </linearGradient>
                  <linearGradient
                    id="kumiaiCircleBackground"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#E8EDFB" />
                    <stop offset="100%" stopColor="#E8EDFB" />
                  </linearGradient>
                </defs>
                <circle
                  cx="55"
                  cy="55"
                  r={normalizedRadius}
                  stroke="url(#kumiaiCircleBackground)"
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={`${maxProgress} ${circumference}`}
                  transform="rotate(133.2 55 55)"
                />
                <circle
                  cx="55"
                  cy="55"
                  r={normalizedRadius}
                  stroke="url(#kumiaiCircleGradient)"
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={`${maxProgress} ${circumference}`}
                  strokeDashoffset={dashOffset}
                  transform="rotate(133.2 55 55)"
                />
                <text
                  x="58"
                  y="65"
                  dominantBaseline="alphabetic"
                  textAnchor="middle"
                  className="font-bold text-[32px] fill-[#5871fe]"
                >
                  {percentage}
                  <tspan className="text-[16px]">%</tspan>
                </text>
              </svg>
            </div>

            {/* Right side - Statistics */}
            <div className="flex flex-col gap-2 w-[152px]">
              {/* 総回答数 */}
              <div className="flex items-center text-[#333333] leading-[1.6]">
                <div className="w-[86px] font-medium text-xs">総回答数</div>
                <div className="font-medium text-xs">：</div>
                <div className="font-medium text-xs">
                  {totalResponders.toLocaleString()}件
                </div>
              </div>

              {/* 任意項目回答数 */}
              <div className="flex items-center text-[#333333] leading-[1.6]">
                <div className="w-[86px] font-medium text-xs">任意項目回答数</div>
                <div className="font-medium text-xs">：</div>
                <div className="font-bold text-xs">
                  <span className="text-[#5871fe] text-xs">{totalCount}</span>
                  <span className="font-medium text-xs">件</span>
                </div>
              </div>
            </div>
          </div>
          {/* 組合員数 */}
          {totalMembers > 0 && (
            <div className="flex items-center justify-center text-[#333333] leading-[1.6] mt-[-4px]">
              <div className="font-medium text-xs">組合員数</div>
              <div className="font-medium text-xs">：</div>
              <div className="font-medium text-xs">
                {totalMembers.toLocaleString()}件
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default KumiaiComparisonChart;
